export interface SurveyQuestion {
  type: string;
  name: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface SurveyPage {
  name: string;
  elements: SurveyQuestion[];
  [key: string]: unknown;
}

export interface SurveySchema {
  pages: SurveyPage[];
  [key: string]: unknown;
}

function hasAnswerValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
}

function stripQuestionVisibility(question: SurveyQuestion): SurveyQuestion {
  const {
    visibleIf: _visibleIf,
    visible: _visible,
    ...questionWithoutVisibility
  } = question;
  return questionWithoutVisibility as SurveyQuestion;
}

function stripPageVisibility(page: SurveyPage): SurveyPage {
  const {
    visibleIf: _visibleIf,
    visible: _visible,
    ...pageWithoutVisibility
  } = page;
  return pageWithoutVisibility as SurveyPage;
}

function stripPageTitleAndDescription(page: SurveyPage): SurveyPage {
  const { title: _title, description: _description, ...rest } = page;
  return rest as SurveyPage;
}

export function filterSurveySchemaByTag(
  schema: SurveySchema,
  tag: string,
): SurveySchema {
  const filteredPages = schema.pages
    .map((page) => {
      const pageStripped = stripPageTitleAndDescription(
        stripPageVisibility(page),
      );

      return {
        ...pageStripped,
        elements: page.elements
          .filter((question) => {
            const questionTags = question.tags || [];
            return questionTags.includes(tag);
          })
          .map(stripQuestionVisibility),
      };
    })
    .filter((page) => page.elements.length > 0);

  return {
    ...schema,
    pages: filteredPages,
  };
}

export function filterSchemaToAnsweredQuestions(
  schema: SurveySchema,
  existingAnswers: Record<string, unknown>,
): SurveySchema {
  const filteredPages = schema.pages
    .map((page) => {
      const answeredElements = page.elements.filter(
        (question) =>
          question.name in existingAnswers &&
          hasAnswerValue(existingAnswers[question.name]),
      );

      const strippedPage = stripPageTitleAndDescription(
        stripPageVisibility(page),
      );

      return {
        ...strippedPage,
        elements: answeredElements.map(stripQuestionVisibility),
      };
    })
    .filter((page) => page.elements.length > 0);

  return {
    ...schema,
    pages: filteredPages,
  };
}

export function stripVisibilityForAnsweredQuestions(
  schema: SurveySchema,
  existingAnswers: Record<string, unknown>,
): SurveySchema {
  const updatedPages = schema.pages.map((page) => {
    const updatedElements = page.elements.map((question) => {
      if (
        question.name in existingAnswers &&
        hasAnswerValue(existingAnswers[question.name])
      ) {
        return stripQuestionVisibility(question);
      }
      return { ...question };
    });

    const pageHasAnsweredQuestion = page.elements.some(
      (question) =>
        question.name in existingAnswers &&
        hasAnswerValue(existingAnswers[question.name]),
    );

    const pageTitleStripped = stripPageTitleAndDescription(page);

    if (pageHasAnsweredQuestion) {
      const strippedPage = stripPageVisibility(pageTitleStripped);
      return { ...strippedPage, elements: updatedElements };
    }

    return { ...pageTitleStripped, elements: updatedElements };
  });

  return {
    ...schema,
    pages: updatedPages,
  };
}
